import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  getAttributeHasValuesError,
  toastApiError,
  type AttributeHasValuesInfo,
} from '@/shared/lib/api-error'

import { ConfirmationDialog } from './confirmation-dialog'

export interface AttributeDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Attribute label, shown in the dialog title. */
  attributeName?: string
  /**
   * Runs the delete mutation. It MUST reject on failure and MUST NOT toast or
   * close the dialog itself - the dialog owns success/error feedback and the
   * two-stage escalation. Pass `force` straight through to the mutation.
   */
  onDelete: (options: { force: boolean }) => Promise<unknown>
  isPending?: boolean
  /** Copy for the first confirmation. Defaults to a generic schema note. */
  description?: ReactNode
  /** Toast shown once the attribute is deleted. */
  successMessage?: string
  /** Toast fallback when the delete fails for a non-recoverable reason. */
  errorFallback?: string
}

/**
 * Shared confirmation flow for deleting a dynamic attribute definition.
 *
 * Stage 1 is the ordinary "Delete attribute?" confirmation. If the backend
 * refuses with the `attribute_has_values` 409 (the attribute still has recorded
 * values on N records), the dialog escalates to Stage 2 - an explicit "delete
 * the attribute AND its recorded values" confirmation - and, on confirm,
 * re-issues the delete with `force: true` to cascade. Any other error is
 * toasted and the dialog stays on Stage 1.
 *
 * Used by every attribute-delete surface (transaction / product / host /
 * warranty / claim / entity attributes) so the destructive cascade behaves
 * identically everywhere.
 */
export function AttributeDeleteDialog({
  open,
  onOpenChange,
  attributeName,
  onDelete,
  isPending = false,
  description = 'This removes the attribute from the schema. Existing displays and mappings that reference it may need to be reviewed.',
  successMessage = 'Attribute deleted.',
  errorFallback = 'Failed to delete attribute.',
}: AttributeDeleteDialogProps) {
  const [cascade, setCascade] = useState<AttributeHasValuesInfo | null>(null)

  // Reset the escalation whenever the dialog is fully closed so re-opening for
  // a different attribute always starts on Stage 1.
  useEffect(() => {
    if (!open) setCascade(null)
  }, [open])

  const label = attributeName ?? 'attribute'

  const close = () => {
    setCascade(null)
    onOpenChange(false)
  }

  const runDelete = async (force: boolean) => {
    try {
      await onDelete({ force })
      toast.success(successMessage)
      close()
    } catch (err) {
      if (!force) {
        const info = getAttributeHasValuesError(err)
        if (info) {
          setCascade(info)
          return
        }
      }
      toastApiError(err, errorFallback)
    }
  }

  if (cascade) {
    const noun = cascade.count === 1 ? cascade.noun : cascade.nounPlural
    return (
      <ConfirmationDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close()
        }}
        title={`Delete ${label} and its recorded values?`}
        description={
          <>
            This attribute has recorded values on{' '}
            <strong>
              {cascade.count} {noun}
            </strong>
            . Deleting it will permanently remove those recorded values too.
            This cannot be undone.
          </>
        }
        confirmLabel="Delete attribute and values"
        pendingLabel="Deleting..."
        destructive
        isPending={isPending}
        onConfirm={() => runDelete(true)}
      />
    )
  }

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title={`Delete ${label}?`}
      description={description}
      confirmLabel="Delete"
      pendingLabel="Deleting..."
      destructive
      isPending={isPending}
      onConfirm={() => runDelete(false)}
    />
  )
}
